import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMode, useSharedProperties } from '@unboared/react';

import { playerLinkUrl, shouldShowPlayerLink } from '~/config/playerLink.config';
import { useFreePlayState } from '~/hooks/freePlay/useFreePlayState';
import { SEASONAL_SHARED_KEY, SeasonalThemeId, seasonEnd } from '~/config/seasonalTheme.config';
import { BatIcon } from '~/components/seasonal/SeasonalDecor';
import { GameRating } from '~/components/GameRating';

import styles from './InterGameScreen.module.css';

/**
 * Thème de saison diffusé par la TV (SeasonalThemeHost), qui a déjà tout filtré (date,
 * bar en mode animé, interrupteur). Garde-fou local : jamais après la fin de la saison.
 */
function useSeasonFromScreen(): SeasonalThemeId | null {
    const { getSharedProperty } = useSharedProperties();
    let value: unknown;
    try {
        value = getSharedProperty(SEASONAL_SHARED_KEY);
    } catch {
        value = undefined; // hôte pas encore connu (cf useFreePlayState)
    }
    return value === 'halloween' && Date.now() < seasonEnd('halloween') ? 'halloween' : null;
}

export function InterGameScreen({ lastGameID, clientID }: { lastGameID?: string; clientID?: string }) {
    const { t } = useTranslation();
    const { mode } = useMode();
    const freePlay = useFreePlayState();
    const showPlayerLink = shouldShowPlayerLink(mode, clientID, freePlay.enabled);
    const seasonal = useSeasonFromScreen();

    // Champ de commentaire ouvert : on masque le lien du bas (clavier, petit écran).
    const [commenting, setCommenting] = useState(false);

    return (
        <div className={`${styles.overlay} ${seasonal ? styles[seasonal] : ''}`}>
            <div className={styles.glow} aria-hidden="true" />
            {seasonal && (
                <div className={styles.seasonSky} aria-hidden="true">
                    <div className={styles.seasonMoon} />
                    <BatIcon className={styles.seasonBat} />
                    <BatIcon className={styles.seasonBat} />
                </div>
            )}
            <div className={styles.card}>
                <header className={styles.wait}>
                    <span className={styles.dots} aria-hidden="true">
                        <span />
                        <span />
                        <span />
                    </span>
                    {seasonal && (
                        <span className={styles.seasonEyebrow}>
                            <span aria-hidden="true">🎃</span>
                            {t(`gamepad.interGame.seasonal.${seasonal}`)}
                        </span>
                    )}
                    <h1 className={styles.title}>{t('gamepad.interGame.title')}</h1>
                    <p className={styles.subtitle}>{t('gamepad.interGame.subtitle')}</p>
                </header>

                {/* We only collect a rating once a game has actually been played (context). */}
                <GameRating gameID={lastGameID} clientID={clientID} onCommentingChange={setCommenting} />
            </div>

            {/* Masqué pendant le commentaire : sur petit écran, clavier ouvert, la place manque. */}
            {showPlayerLink && clientID && !commenting && (
                <a
                    className={styles.playerLink}
                    href={playerLinkUrl(clientID)}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <span>{t('gamepad.interGame.playerLinkQuestion')}</span>
                    <span className={styles.playerLinkCta}>{t('gamepad.interGame.playerLinkCta')}</span>
                </a>
            )}
        </div>
    );
}
